import { useEffect, useRef, useState } from 'react';

import { isSpeechSupported, startDictation, type DictationHandle } from '../lib/speech';
import { MicIcon, StopIcon } from './Icons';
import { useT } from '../lib/i18n';

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label: string;
  rows?: number;
}

/**
 * A textarea you can dictate into. Interim results render as you speak and are
 * replaced when the phrase is finalised, so the field never shows duplicated
 * text mid-sentence.
 */
export function VoiceInput({ value, onChange, placeholder, label, rows }: Props) {
  const t = useT();
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState('');
  const handleRef = useRef<DictationHandle | undefined>(undefined);
  // The committed text at the moment dictation began; finals append to this.
  const baseRef = useRef('');
  const supported = isSpeechSupported();

  useEffect(() => () => handleRef.current?.stop(), []);

  const stop = () => {
    handleRef.current?.stop();
    handleRef.current = undefined;
    setListening(false);
    setInterim('');
  };

  const start = () => {
    setError('');
    baseRef.current = value.trim();

    const handle = startDictation({
      onResult: ({ transcript, isFinal }) => {
        if (isFinal) {
          const next = [baseRef.current, transcript.trim()].filter(Boolean).join(' ');
          baseRef.current = next;
          setInterim('');
          onChange(next);
        } else {
          setInterim(transcript);
        }
      },
      onError: (message) => {
        setError(message);
        stop();
      },
      onEnd: () => {
        setListening(false);
        setInterim('');
        handleRef.current = undefined;
      },
    });

    if (!handle) {
      setError('Dictation is not supported in this browser.');
      return;
    }
    handleRef.current = handle;
    setListening(true);
  };

  const shown = listening && interim ? `${value ? `${value} ` : ''}${interim}` : value;

  return (
    <div className="field">
      <div className="row row--between">
        <label htmlFor="voice-input">{label}</label>
        {supported && (
          <button
            type="button"
            className={`btn btn--sm ${listening ? 'btn--danger' : 'btn--ghost'}`}
            onClick={listening ? stop : start}
          >
            {listening ? (
              <>
                <span className="rec-dot" aria-hidden="true" />
                <StopIcon /> {t('voice.stop')}
              </>
            ) : (
              <>
                <MicIcon /> {t('voice.dictate')}
              </>
            )}
          </button>
        )}
      </div>

      <textarea
        id="voice-input"
        className="textarea"
        rows={rows ?? 4}
        value={shown}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        // Interim text is transient and would be overwritten by typing.
        readOnly={listening && Boolean(interim)}
      />

      {listening && <p className="faint pulse">{t('voice.listening')}</p>}
      {error && (
        <p className="faint" style={{ color: 'var(--danger)' }}>
          {error}
        </p>
      )}
      {!supported && (
        <p className="faint">Dictation needs Chrome, Edge or Safari. Typing works everywhere.</p>
      )}
    </div>
  );
}
